import { logger } from './logger';

// Types
export type TaskStatus = string;
export type ColumnColor = string;
export type DashboardSection =
  | 'highlighted'
  | 'overdue'
  | 'today'
  | 'thisWeek'
  | 'upcoming'
  | string;

/**
 * Extracts a base color name from a column color value, which could be
 * a gradient string, a class name, or a direct color name
 * @param colorString The column color string to process
 * @returns A normalized color name (blue, red, etc.) or null if not recognized
 */
export function extractBaseColor(colorString: string | undefined | null): string | null {
  if (!colorString) return null;

  // If it's already a simple color name, return it
  const simpleColors = [
    'blue',
    'purple',
    'pink',
    'red',
    'orange',
    'yellow',
    'green',
    'cyan',
    'indigo',
    'teal',
    'gray',
  ];

  if (simpleColors.includes(colorString)) {
    return colorString;
  }

  // Check for color in gradient string format
  for (const color of simpleColors) {
    if (colorString.includes(color)) {
      return color;
    }
  }

  // If no matches, return null
  return null;
}

// Color mapping interfaces
export interface ColorMappings {
  bg: Record<string, string>;
  text: Record<string, string>;
  border: Record<string, string>;
  tag: Record<string, string>;
  highlight: Record<string, string>;
  icon: Record<string, string>;
  dot: Record<string, string>; // For column indicator dots with brighter colors
}

/**
 * IMPORTANT: This mapping should ONLY be used for special cases where direct Tailwind classes
 * cannot be applied, such as third-party components that require direct CSS values.
 *
 * For all normal React components, use the getStatusColor function to get Tailwind classes instead.
 *
 * Use case examples:
 * - Calendar integration (React Big Calendar)
 * - External libraries that don't support Tailwind
 * - Canvas or SVG rendering
 */
export const tailwindToHexColor = (tailwindClass: string): string => {
  const colorMap: Record<string, string> = {
    // Background colors
    'bg-blue-100': '#dbeafe',
    'bg-green-100': '#dcfce7',
    'bg-yellow-100': '#fef9c3',
    'bg-red-100': '#fee2e2',
    'bg-purple-100': '#f3e8ff',
    'bg-pink-100': '#fce7f3',
    'bg-orange-100': '#ffedd5',
    'bg-teal-100': '#ccfbf1',
    'bg-cyan-100': '#cffafe',
    'bg-indigo-100': '#e0e7ff',
    'bg-gray-100': '#f3f4f6',

    // Border colors
    'border-blue-500/50': '#3b82f6',
    'border-green-500/50': '#10b981',
    'border-yellow-500/50': '#eab308',
    'border-red-500/50': '#ef4444',
    'border-purple-500/50': '#8b5cf6',
    'border-pink-500/50': '#ec4899',
    'border-orange-500/50': '#f97316',
    'border-teal-500/50': '#14b8a6',
    'border-cyan-500/50': '#06b6d4',
    'border-indigo-500/50': '#6366f1',
    'border-gray-500/50': '#6b7280',

    // Highlighted border colors
    'border-blue-600': '#2563eb',
    'border-green-600': '#059669',
    'border-yellow-600': '#ca8a04',
    'border-red-600': '#dc2626',
    'border-purple-600': '#7c3aed',
    'border-pink-600': '#db2777',
    'border-orange-600': '#ea580c',
    'border-teal-600': '#0d9488',
    'border-cyan-600': '#0891b2',
    'border-indigo-600': '#4f46e5',
    'border-gray-600': '#4b5563',

    // Text colors
    'text-blue-800': '#1e40af',
    'text-green-800': '#166534',
    'text-yellow-800': '#854d0e',
    'text-red-800': '#991b1b',
    'text-purple-800': '#6b21a8',
    'text-pink-800': '#9d174d',
    'text-orange-800': '#9a3412',
    'text-teal-800': '#115e59',
    'text-cyan-800': '#155e75',
    'text-indigo-800': '#3730a3',
    'text-gray-800': '#1f2937',
  };

  return colorMap[tailwindClass] || '#6b7280'; // Default to gray-500 if no match
};

/**
 * Standard task status colors
 */
export const STANDARD_STATUS_COLORS: Record<string, string> = {
  todo: 'blue',
  doing: 'yellow',
  done: 'green',
  default: 'gray',
};

/**
 * Maps task column colors to various CSS color classes
 */
export const COLOR_MAPPINGS: ColorMappings = {
  // Background colors (lighter shade)
  bg: {
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    yellow: 'bg-yellow-100',
    red: 'bg-red-100',
    purple: 'bg-purple-100',
    pink: 'bg-pink-100',
    orange: 'bg-orange-100',
    teal: 'bg-teal-100',
    cyan: 'bg-cyan-100',
    indigo: 'bg-indigo-100',
    gray: 'bg-gray-100',
  },

  // Text colors
  text: {
    blue: 'text-blue-800',
    green: 'text-green-800',
    yellow: 'text-yellow-800',
    red: 'text-red-800',
    purple: 'text-purple-800',
    pink: 'text-pink-800',
    orange: 'text-orange-800',
    teal: 'text-teal-800',
    cyan: 'text-cyan-800',
    indigo: 'text-indigo-800',
    gray: 'text-gray-800',
  },

  // Border colors with medium opacity
  border: {
    blue: 'border-blue-500',
    green: 'border-green-500',
    yellow: 'border-yellow-500',
    red: 'border-red-500',
    purple: 'border-purple-500',
    pink: 'border-pink-500',
    orange: 'border-orange-500',
    teal: 'border-teal-500',
    cyan: 'border-cyan-500',
    indigo: 'border-indigo-500',
    gray: 'border-gray-500',
  },

  // Tag colors (for badges with matching bg and text)
  tag: {
    blue: 'bg-blue-100/80 text-blue-800 border border-blue-100/80',
    green: 'bg-green-100/80 text-green-800 border border-green-100/80',
    yellow: 'bg-yellow-100/80 text-yellow-800 border border-yellow-100/80',
    red: 'bg-red-100/80 text-red-800 border border-red-100/80',
    purple: 'bg-purple-100/80 text-purple-800 border border-purple-100/80',
    pink: 'bg-pink-100/80 text-pink-800 border border-pink-100/80',
    orange: 'bg-orange-100/80 text-orange-800 border border-orange-100/80',
    teal: 'bg-teal-100/80 text-teal-800 border border-teal-100/80',
    cyan: 'bg-cyan-100/80 text-cyan-800 border border-cyan-100/80',
    indigo: 'bg-indigo-100/80 text-indigo-800 border border-indigo-100/80',
    gray: 'bg-gray-100/80 text-gray-800 border border-gray-100/80',
  },

  // Highlight colors (darker borders for highlighted items)
  highlight: {
    blue: 'border-blue-600',
    green: 'border-green-600',
    yellow: 'border-yellow-600',
    red: 'border-red-600',
    purple: 'border-purple-600',
    pink: 'border-pink-600',
    orange: 'border-orange-600',
    teal: 'border-teal-600',
    cyan: 'border-cyan-600',
    indigo: 'border-indigo-600',
    gray: 'border-gray-600',
  },

  // Icon colors (for UI elements)
  icon: {
    blue: 'text-blue-500',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    red: 'text-red-500',
    purple: 'text-purple-500',
    pink: 'text-pink-500',
    orange: 'text-orange-500',
    teal: 'text-teal-500',
    cyan: 'text-cyan-500',
    indigo: 'text-indigo-500',
    gray: 'text-gray-500',
  },
  
  // Bright dot colors for indicators (solid colors without opacity)
  dot: {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    orange: 'bg-orange-500',
    teal: 'bg-teal-500',
    cyan: 'bg-cyan-500',
    indigo: 'bg-indigo-500',
    gray: 'bg-gray-500',
  },
};

/**
 * Dashboard section colors
 */
export const DASHBOARD_SECTION_COLORS: Record<string, string> = {
  highlighted: 'yellow',
  overdue: 'red',
  today: 'green',
  thisWeek: 'blue',
  upcoming: 'indigo',
  default: 'gray',
};

/**
 * Gets the appropriate color based on task status and columns configuration
 * @param status The task status
 * @param columns Optional column configuration list
 * @param colorType Type of color class to return (bg, text, border, etc.)
 * @param isHighlighted Whether the item is highlighted
 * @returns CSS class for the specified color type
 */
export function getStatusColor(
  status: TaskStatus,
  columns?: { status: string; color?: string }[],
  colorType: keyof ColorMappings = 'bg',
  isHighlighted = false
): string {
  try {
    // Handle highlighted state for border colors
    if (colorType === 'border' && isHighlighted) {
      colorType = 'highlight';
    }

    // Try to find matching column with color
    if (columns) {
      const taskColumn = columns.find(col => col.status === status);
      if (taskColumn?.color) {
        // Extract the base color from the column color
        const baseColor = extractBaseColor(taskColumn.color);
        if (baseColor && COLOR_MAPPINGS[colorType][baseColor]) {
          return COLOR_MAPPINGS[colorType][baseColor];
        }
      }
    }

    // If no column match, use standard colors
    // Check for custom status (they usually include 'custom_')
    const isCustomStatus = status.includes('custom_');
    // For custom statuses that aren't found in the columns, assign a color based on the status
    if (isCustomStatus) {
      // Generate deterministic color based on the status string
      const colors = [
        'blue',
        'purple',
        'pink',
        'red',
        'orange',
        'yellow',
        'green',
        'cyan',
        'teal',
        'indigo',
      ];
      // Use a simple hash function to determine color
      let hash = 0;
      for (let i = 0; i < status.length; i++) {
        hash = (hash + status.charCodeAt(i)) % colors.length;
      }
      const colorKey = colors[hash];
      return COLOR_MAPPINGS[colorType][colorKey] || COLOR_MAPPINGS[colorType].gray;
    }

    // Fall back to standard colors for known statuses
    const baseColor = STANDARD_STATUS_COLORS[status] || STANDARD_STATUS_COLORS.default;
    return COLOR_MAPPINGS[colorType][baseColor] || COLOR_MAPPINGS[colorType].gray;
  } catch (error) {
    logger.warn('Error getting status color', {
      status,
      colorType,
      error: error instanceof Error ? error.message : String(error),
    });

    // Fallback to gray if anything goes wrong
    return COLOR_MAPPINGS[colorType].gray;
  }
}

/**
 * Gets the appropriate color class based on dashboard section type
 * @param section Dashboard section type
 * @param colorType Type of color class to return (bg, text, border, etc.)
 * @returns CSS class for the specified color type
 */
export function getDashboardSectionColor(
  section: DashboardSection,
  colorType: keyof ColorMappings = 'bg'
): string {
  try {
    const baseColor = DASHBOARD_SECTION_COLORS[section] || DASHBOARD_SECTION_COLORS.default;
    return COLOR_MAPPINGS[colorType][baseColor] || COLOR_MAPPINGS[colorType].gray;
  } catch (error) {
    logger.warn('Error getting dashboard section color', {
      section,
      colorType,
      error: error instanceof Error ? error.message : String(error),
    });

    // Fallback to gray if anything goes wrong
    return COLOR_MAPPINGS[colorType].gray;
  }
}

/**
 * Creates a deterministic color class based on an ID or string
 * @param id The string to generate a color for
 * @param colorType Type of color class to return (bg, text, border, etc.)
 * @returns CSS class for the specified color type
 */
export function getDeterministicColor(id: string, colorType: keyof ColorMappings = 'bg'): string {
  try {
    // List of available colors excluding gray (which we use as fallback)
    const colors = Object.keys(COLOR_MAPPINGS[colorType]).filter(c => c !== 'gray');

    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash + id.charCodeAt(i)) % colors.length;
    }

    const baseColor = colors[hash];
    return COLOR_MAPPINGS[colorType][baseColor] || COLOR_MAPPINGS[colorType].gray;
  } catch (error) {
    logger.warn('Error getting deterministic color', {
      id,
      colorType,
      error: error instanceof Error ? error.message : String(error),
    });

    // Fallback to gray if anything goes wrong
    return COLOR_MAPPINGS[colorType].gray;
  }
}
